import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { useCycleStore } from '@cycle-hangars/store/store';
import { BinaryQuestionTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, scrollTop, stringFieldSchema } from '@utils';
import { TYPE_OF_ISSUE_ERROR } from '@cycle-hangars/constants/errorMessages';
import { ZodError } from 'zod';
import { cycleHangarIssueTypes } from '@cycle-hangars/store/storeSlices/issueDetails';

export const SelectCycleHangarIssue: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    knowsCycleHangarId,
    cycleHangarIssue,
    updateCycleHangarIssue,
    resetIssueDetailsInput,
  } = useCycleStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'What is the issue?';

  const options = [
    {
      id: 'BrokenLock',
      name: 'BrokenLock',
      labelText: 'Broken lock',
      isDefault: cycleHangarIssue === 'BrokenLock',
    },
    {
      id: 'BikeTheft',
      name: 'BikeTheft',
      labelText: 'Bike theft',
      isDefault: cycleHangarIssue === 'BikeTheft',
    },
    {
      id: 'DamagedOrVandalism',
      name: 'DamagedOrVandalism',
      labelText: 'Damaged or vandalised',
      isDefault: cycleHangarIssue === 'DamagedOrVandalism',
    },
    {
      id: 'Cleaning',
      name: 'Cleaning',
      labelText: 'Cleaning needed',
      isDefault: cycleHangarIssue === 'Cleaning',
    },
    {
      id: 'AbandonedOrUnauthorisedBike',
      name: 'AbandonedOrUnauthorisedBike',
      labelText: 'Abandoned or unauthorised bikes',
      isDefault: cycleHangarIssue === 'AbandonedOrUnauthorisedBike',
    },
    {
      id: 'SomethingElse',
      name: 'somethingElse',
      labelText: 'It’s something else',
      isDefault: cycleHangarIssue === 'SomethingElse',
    },
  ];

  const handleChange = (selection: string) => {
    const updatedSelection = options.filter(
      (result) => result.labelText === selection,
    )[0].id;
    updateCycleHangarIssue(updatedSelection as cycleHangarIssueTypes);
    resetIssueDetailsInput();
    setErrorMessages([]);
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: () => {
      if (knowsCycleHangarId === 'Yes') {
        navigate(RoutesList.DETAILS);
      } else {
        navigate(RoutesList.ISSUE_LOCATION);
      }
      scrollTop();
    },
  };

  const handleNavigate = () => {
    switch (cycleHangarIssue) {
      case 'BrokenLock':
        navigate(RoutesList.CYCLE_HANGAR_SECURE);
        break;
      case 'BikeTheft':
        navigate(RoutesList.CYCLE_HANGAR_SECURE);
        break;
      case 'DamagedOrVandalism':
        navigate(RoutesList.CYCLE_HANGAR_SECURE);
        break;
      case 'Cleaning':
        navigate(RoutesList.CLEANING);
        break;
      case 'AbandonedOrUnauthorisedBike':
        navigate(RoutesList.ABANDONED_OR_UNAUTHORISED);
        break;
      case 'SomethingElse':
        navigate(RoutesList.TELL_US_MORE);
      default:
        return;
    }
  };

  const buttonNext = {
    onClick: async () => {
      try {
        const validateIssueSelection = stringFieldSchema(
          'cycleHangarIssueSelection',
          TYPE_OF_ISSUE_ERROR.required.message,
        ).parse({
          cycleHangarIssueSelection: cycleHangarIssue,
        });
        handleNavigate();
        scrollTop();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            message: TYPE_OF_ISSUE_ERROR.required.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <BinaryQuestionTemplate
      title={title}
      options={options}
      onChange={handleChange}
      errorPath="cycleHangarIssueSelection"
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
